/**
 * Adapter for the ai-in-finance backend.
 *
 * This is the ONLY layer that knows where finance functionality comes from.
 * Today that is the ai-in-finance Vercel app; it is a temporary backend that
 * will be replaced by our own Kognitos services. Pages and hooks depend on the
 * domain hooks in `./hooks`, never on this module's transport details, so the
 * eventual swap touches only this file (and the per-domain mappers).
 *
 * Transport notes:
 * - The local dev server routes through the Vite `/finance-proxy` proxy (see
 *   vite.config.ts) to dodge CORS. Deployed builds read the backend URL from the
 *   injected runtime config (`window.__RUNTIME_CONFIG__.financeBackendUrl`, set
 *   per environment by docker-entrypoint.sh) and call it directly, which requires
 *   the backend to send CORS headers for our origin and the CSP `connect-src` to
 *   allow that origin.
 * - Auth is pluggable via `setFinanceAuthHeadersProvider`; `FinanceAuthBridge`
 *   registers Bumblebee's Auth0 token, and the default is no headers.
 */
import axios from 'axios';

import { getRuntimeConfig } from '@/config/runtimeConfig';
import { getTraceContext } from '@/shared/utils/trace-context';
import { isE2ETestMode } from '@/shared/auth/utils/e2e';

import type {
  IApproveJournalEntryRequest,
  IApproveJournalEntryResponse,
  ICreateJournalEntryResponse,
  IDiscoveryBenchResponse,
  IRunDiscoveryScanResponse,
  IEvidenceVaultResponse,
  IJournalEntryCreateRequest,
  IJournalEntryDraftWire,
  IJournalEntryPatchRequest,
  IListJournalEntriesResponse,
  IListLearningsResponse,
  IPatchJournalEntryResponse,
  IPlaceAgentRequestWire,
  IPlaceAgentResponse,
  IListAuditEventsResponse,
  IListSopVaultResponse,
  IAgentSopResponse,
  IListFindingsResponse,
  IFleetOrgResponse,
  IAgentRegistryResponse,
  ISetAgentEnabledRequest,
  IAgentSchedulesResponse,
  IListExceptionsResponse,
  IAgentCatalogResponse,
  IProcessCatalogResponse,
  IMatchCatalogRequest,
  IMatchCatalogResponse,
  IAgentPolicyResponse,
  ISetAgentPolicyRequest,
  IPatchAgentIdentityRequest,
  IPatchAgentIdentityResponse,
  ISetAgentCommissionRequest,
  IWireHeartbeatSchedule,
  IWireInvoiceEntity,
  IWirePurchaseOrderEntity,
  IWireVendorEntity,
  IApplyResolutionRequest,
  IApplyResolutionResponse,
  IListMailboxesResponse,
  IStagedByRunResponse,
  IWireDraftInput,
  TWireMailboxRunsResponse,
  TWireMailboxSummary,
  ICreateAgentDraftRequest,
  ICreateAgentDraftResponse,
} from './types';

/** Relative prefix proxied to the backend by the Vite dev server. */
export const FINANCE_API_PROXY_PREFIX = '/finance-proxy';

/**
 * Resolve the base URL for finance API calls.
 * - Local dev server: the `/finance-proxy` proxy prefix (CORS handled by Vite).
 * - Deployed builds: the absolute `financeBackendUrl` from the runtime config.
 *
 * If it is missing in a deployed build we warn rather than fail silently: an
 * empty base URL makes axios resolve finance paths against Bumblebee's own
 * origin, so calls would 404 against the wrong backend with no obvious cause.
 */
export function resolveFinanceBaseUrl(): string {
  if (import.meta.env.DEV) return FINANCE_API_PROXY_PREFIX;
  const url = getRuntimeConfig().financeBackendUrl;
  if (!url) {
    console.warn(
      '[finance] financeBackendUrl is not set in the runtime config — finance ' +
        "API calls will resolve against the app's own origin and fail. Set " +
        'FINANCE_BACKEND_URL for this environment.',
    );
    return '';
  }
  return url;
}

/** Headers map a provider may contribute to every finance request. */
type TFinanceAuthHeaders = Record<string, string>;
type TFinanceAuthHeadersProvider = () =>
  | TFinanceAuthHeaders
  | Promise<TFinanceAuthHeaders>;

/** Default: the demo backend needs no auth. */
let authHeadersProvider: TFinanceAuthHeadersProvider = () => ({});

/**
 * Register how finance requests are authenticated. Lets us start with no auth
 * for the demo and harden later (bearer/shared token) without touching pages.
 */
export function setFinanceAuthHeadersProvider(
  provider: TFinanceAuthHeadersProvider,
): void {
  authHeadersProvider = provider;
}

/** Resolve the auth headers to attach to a finance request. */
export function getFinanceAuthHeaders(): Promise<TFinanceAuthHeaders> {
  return Promise.resolve(authHeadersProvider());
}

/**
 * Build the header set every finance request carries: the trace id, then either
 * the E2E marker (test mode, no auth) or the registered auth headers. Shared by
 * the axios interceptor below and by non-axios transports (e.g. the connect SSE
 * stream, which must use `fetch`) so all finance requests stay authenticated the
 * same way. This is the single place request auth is assembled.
 */
export async function buildFinanceRequestHeaders(): Promise<
  Record<string, string>
> {
  const headers: Record<string, string> = {
    'x-kog-request-id': getTraceContext().requestId,
  };
  if (isE2ETestMode()) {
    headers['X-E2E-Test-Mode'] = 'true';
    return headers;
  }
  Object.assign(headers, await getFinanceAuthHeaders());
  return headers;
}

/** Shared axios instance for the ai-in-finance backend. */
export const financeApi = axios.create();

financeApi.interceptors.request.use(async config => {
  // Resolve the base URL per-request rather than at axios.create() time so the
  // runtime config is only read when a finance call is actually made. This
  // preserves lazy evaluation: the URL stays mockable in tests and correct if
  // the runtime config is injected after this module is first imported.
  config.baseURL = resolveFinanceBaseUrl();
  const headers = await buildFinanceRequestHeaders();
  for (const [key, value] of Object.entries(headers)) {
    config.headers[key] = value;
  }
  return config;
});

// ── Endpoints ───────────────────────────────────────────────────────────────

/** `GET /api/learnings` — durable tribal knowledge captured on the Workbench. */
export async function listLearnings(
  signal?: AbortSignal,
): Promise<IListLearningsResponse> {
  const { data } = await financeApi.get<IListLearningsResponse>(
    '/api/learnings',
    { signal },
  );
  return data;
}

/** `DELETE /api/learnings?id=<id>` — retract a learning. */
export async function deleteLearning(id: string): Promise<void> {
  await financeApi.delete('/api/learnings', { params: { id } });
}

/** `GET /api/fleet/org` — the fleet's live org overlay (commission + roster). */
export async function getFleetOrg(
  signal?: AbortSignal,
): Promise<IFleetOrgResponse> {
  const { data } = await financeApi.get<IFleetOrgResponse>('/api/fleet/org', {
    signal,
  });
  return data;
}

/** `GET /api/evidence` — the fleet's indexed evidence, faceted + scored. */
export async function listEvidence(
  signal?: AbortSignal,
): Promise<IEvidenceVaultResponse> {
  const { data } = await financeApi.get<IEvidenceVaultResponse>(
    '/api/evidence',
    { signal },
  );
  return data;
}

/**
 * `GET /api/journal-entries/register` — every drafted/staged/posted journal
 * entry, with its lines and lineage. Shared by the JE register and Approvals.
 */
export async function listJournalEntries(
  signal?: AbortSignal,
): Promise<IListJournalEntriesResponse> {
  const { data } = await financeApi.get<IListJournalEntriesResponse>(
    '/api/journal-entries/register',
    { signal },
  );
  return data;
}

/**
 * `GET /api/journal-entries/register/{id}` — one drafted journal entry with its
 * full lines + rich lineage. Backs the JE detail page. The backend wraps the
 * row as `{ draft }` (a 404 `{ error: 'not_found' }` surfaces as an axios error).
 */
export async function getJournalEntry(
  id: string,
  signal?: AbortSignal,
): Promise<{ draft: IJournalEntryDraftWire | null }> {
  const { data } = await financeApi.get<{
    draft: IJournalEntryDraftWire | null;
  }>(`/api/journal-entries/register/${id}`, { signal });
  return data;
}

/**
 * `POST /api/journal-entries/register` — create a manual journal-entry draft.
 * The backend validates + balances the body (`normalize-draft`); an unbalanced
 * or empty entry is rejected (400). Returns `{ ok, draft }` (201) with the
 * created draft (which carries the assigned `id`).
 */
export async function createJournalEntry(
  body: IJournalEntryCreateRequest,
): Promise<ICreateJournalEntryResponse> {
  const { data } = await financeApi.post<ICreateJournalEntryResponse>(
    '/api/journal-entries/register',
    body,
  );
  return data;
}

/**
 * `PATCH /api/journal-entries/register/{id}` — a lifecycle transition. The
 * transition is chosen by `patch.action` (stage / reject / reopen / archive /
 * unarchive), NOT by setting `status` directly. `reject` reads `patch.reason`.
 */
export async function patchJournalEntry(
  id: string,
  patch: IJournalEntryPatchRequest,
): Promise<IPatchJournalEntryResponse> {
  const { data } = await financeApi.patch<IPatchJournalEntryResponse>(
    `/api/journal-entries/register/${id}`,
    patch,
  );
  return data;
}

/**
 * `POST /api/journal-entries/register/{id}/approve` — approve + post to SAP
 * (`mode: 'actual'`, the default) or validate + simulate only
 * (`mode: 'dry_run'`, which leaves the lifecycle untouched).
 */
export async function approveJournalEntry(
  id: string,
  body: IApproveJournalEntryRequest = {},
): Promise<IApproveJournalEntryResponse> {
  const { data } = await financeApi.post<IApproveJournalEntryResponse>(
    `/api/journal-entries/register/${id}/approve`,
    body,
  );
  return data;
}

/** `GET /api/agents/discovery` — Cloudburst agents awaiting taxonomy placement. */
export async function getBenchEntries(
  signal?: AbortSignal,
): Promise<IDiscoveryBenchResponse> {
  const { data } = await financeApi.get<IDiscoveryBenchResponse>(
    '/api/agents/discovery',
    { signal },
  );
  return data;
}

/**
 * `POST /api/agents/discovery` — run the full discovery pipeline, which
 * LLM-classifies unplaced Cloudburst agents onto the bench. Idempotent: already
 * -suggested agents are served from cache without extra LLM calls. Callers
 * refetch the bench (`getBenchEntries`) afterwards to show the results.
 */
export async function runDiscoveryScan(): Promise<IRunDiscoveryScanResponse> {
  const { data } = await financeApi.post<IRunDiscoveryScanResponse>(
    '/api/agents/discovery',
  );
  return data;
}

/** `POST /api/agents/discovery/place` — promote a bench agent into the catalog. */
export async function placeAgent(
  req: IPlaceAgentRequestWire,
): Promise<IPlaceAgentResponse> {
  const { data } = await financeApi.post<IPlaceAgentResponse>(
    '/api/agents/discovery/place',
    req,
  );
  return data;
}

/** `GET /api/audit` — the tenant's immutable audit feed (audit rows + traces). */
export async function listAuditEvents(
  signal?: AbortSignal,
): Promise<IListAuditEventsResponse> {
  const { data } = await financeApi.get<IListAuditEventsResponse>(
    '/api/audit',
    {
      signal,
    },
  );
  return data;
}

/** `GET /api/sop-vault` — every agent's live source plus vetted templates. */
export async function listSopVault(
  signal?: AbortSignal,
): Promise<IListSopVaultResponse> {
  const { data } = await financeApi.get<IListSopVaultResponse>(
    '/api/sop-vault',
    { signal },
  );
  return data;
}

/** `GET /api/agents/{agentKey}/sop` — a live agent's compiled SOP body. */
export async function getAgentSop(
  agentKey: string,
  signal?: AbortSignal,
): Promise<IAgentSopResponse> {
  const { data } = await financeApi.get<IAgentSopResponse>(
    `/api/agents/${encodeURIComponent(agentKey)}/sop`,
    { signal },
  );
  return data;
}

// ── Findings ──────────────────────────────────────────────────────────────────

/** `GET /api/findings` — all findings, bucketed into resolved/investigating. */
export async function listFindings(
  signal?: AbortSignal,
): Promise<IListFindingsResponse> {
  const { data } = await financeApi.get<IListFindingsResponse>(
    '/api/findings',
    { signal },
  );
  return data;
}

// ── Agent registry ────────────────────────────────────────────────────────────

/** `GET /api/agents/registry` — which agent keys are enabled in the registry. */
export async function getAgentRegistry(
  signal?: AbortSignal,
): Promise<IAgentRegistryResponse> {
  const { data } = await financeApi.get<IAgentRegistryResponse>(
    '/api/agents/registry',
    { signal },
  );
  return data;
}

/** `POST /api/agents/registry` — enable/disable an agent (the Catalog toggle). */
export async function setAgentEnabled(
  body: ISetAgentEnabledRequest,
): Promise<void> {
  await financeApi.post('/api/agents/registry', body);
}

// ── Agent schedules ───────────────────────────────────────────────────────────

/**
 * `GET /api/agents/schedules?keys=…` — live cadence per agent key (null when the
 * agent has no live schedule). The backend requires the `keys` param, so an
 * empty request is short-circuited to an empty map rather than hitting the API.
 */
export async function getAgentSchedules(
  keys: string[],
  signal?: AbortSignal,
): Promise<IAgentSchedulesResponse> {
  if (keys.length === 0) return { schedules: {} };
  const { data } = await financeApi.get<IAgentSchedulesResponse>(
    '/api/agents/schedules',
    { params: { keys: keys.join(',') }, signal },
  );
  return data;
}

// ── Exceptions ────────────────────────────────────────────────────────────────

/** `GET /api/exceptions` — the tenant's match/control exceptions (open + closed). */
export async function listExceptions(
  signal?: AbortSignal,
): Promise<IListExceptionsResponse> {
  const { data } = await financeApi.get<IListExceptionsResponse>(
    '/api/exceptions',
    { signal },
  );
  return data;
}

// ── Agent catalog ─────────────────────────────────────────────────────────────

/** `GET /api/agents/catalog` — the full shipped agent spec library (~130 specs). */
export async function getAgentCatalog(
  signal?: AbortSignal,
): Promise<IAgentCatalogResponse> {
  const { data } = await financeApi.get<IAgentCatalogResponse>(
    '/api/agents/catalog',
    { signal },
  );
  return data;
}

// ── Agent policy (auto-resolve) ───────────────────────────────────────────────

/** `GET /api/agents/[key]/policy` — one agent's auto-resolve policy. */
export async function getAgentPolicy(
  agentKey: string,
  signal?: AbortSignal,
): Promise<IAgentPolicyResponse> {
  const { data } = await financeApi.get<IAgentPolicyResponse>(
    `/api/agents/${encodeURIComponent(agentKey)}/policy`,
    { signal },
  );
  return data;
}

/** `POST /api/agents/[key]/policy` — persist a partial auto-resolve policy update. */
export async function setAgentPolicy(
  agentKey: string,
  body: ISetAgentPolicyRequest,
): Promise<void> {
  await financeApi.post(
    `/api/agents/${encodeURIComponent(agentKey)}/policy`,
    body,
  );
}

// ── Agent identity ────────────────────────────────────────────────────────────

/** `PATCH /api/agents/[key]/identity` — override an agent's name/description. */
export async function patchAgentIdentity(
  agentKey: string,
  body: IPatchAgentIdentityRequest,
): Promise<IPatchAgentIdentityResponse> {
  const { data } = await financeApi.patch<IPatchAgentIdentityResponse>(
    `/api/agents/${encodeURIComponent(agentKey)}/identity`,
    body,
  );
  return data;
}

// ── Fleet commission ──────────────────────────────────────────────────────────

/** `POST /api/fleet` (`action: 'commission'`) — commission/decommission an agent. */
export async function setAgentCommission(
  body: ISetAgentCommissionRequest,
): Promise<void> {
  await financeApi.post('/api/fleet', {
    action: 'commission',
    agentKey: body.agentKey,
    enabled: body.enabled,
  });
}

// ── Agent schedule (write) ────────────────────────────────────────────────────

/** `PUT /api/agents/[key]/schedule` — persist one agent's live cadence. */
export async function putAgentSchedule(
  agentKey: string,
  schedule: IWireHeartbeatSchedule,
): Promise<void> {
  await financeApi.put(`/api/agents/${encodeURIComponent(agentKey)}/schedule`, {
    schedule,
  });
}

// ── Exception detail: live entities + resolver apply ──────────────────────────

/** `GET /api/invoices/[id]` — read-through to the live invoice row. */
export async function getFinanceInvoice(
  id: string,
  signal?: AbortSignal,
): Promise<IWireInvoiceEntity> {
  const { data } = await financeApi.get<IWireInvoiceEntity>(
    `/api/invoices/${id}`,
    { signal },
  );
  return data;
}

/** `GET /api/purchase_orders/[id]` — read-through to the live PO row. */
export async function getFinancePurchaseOrder(
  id: string,
  signal?: AbortSignal,
): Promise<IWirePurchaseOrderEntity> {
  const { data } = await financeApi.get<IWirePurchaseOrderEntity>(
    `/api/purchase_orders/${id}`,
    { signal },
  );
  return data;
}

/** `GET /api/vendors/[id]` — read-through to the live vendor row. */
export async function getFinanceVendor(
  id: string,
  signal?: AbortSignal,
): Promise<IWireVendorEntity> {
  const { data } = await financeApi.get<IWireVendorEntity>(
    `/api/vendors/${id}`,
    { signal },
  );
  return data;
}

/**
 * `POST /api/resolver/apply_resolution` — apply a matcher-proposed fix. Runs the
 * apply verb against the backend and writes an audit row to `resolver_resolutions`.
 */
export async function applyResolution(
  body: IApplyResolutionRequest,
): Promise<IApplyResolutionResponse> {
  const { data } = await financeApi.post<IApplyResolutionResponse>(
    '/api/resolver/apply_resolution',
    body,
  );
  return data;
}

/** `GET /api/mailbox` — the inbound-email intake surfaces (mailbox registry). */
export async function listMailboxes(
  signal?: AbortSignal,
): Promise<IListMailboxesResponse> {
  const { data } = await financeApi.get<IListMailboxesResponse>(
    '/api/mailbox',
    {
      signal,
    },
  );
  return data;
}

/**
 * `GET /api/traces/activity` (SSE) — open the authenticated live agent-activity
 * stream. Returns the raw `Response` so the caller drives `res.body.getReader()`
 * and parses the SSE frames itself.
 *
 * Uses `fetch`, not the shared `useStream`/`createStream` library, `EventSource`,
 * or axios. The unified library is kept out of this seam by design: this module
 * is the sole owner of finance transport so the eventual backend swap stays
 * local, and the temporary ai-in-finance backend doesn't speak that library's
 * Last-Event-ID / error-classification conventions. `fetch` (over `EventSource`)
 * lets the shared auth + trace headers ride along — `EventSource` has no header
 * channel, and the tenant is resolved from those headers, not a query param.
 * Mirrors the ingest SSE transport in `connect-client.ts`.
 */
export async function openActivityStream(
  signal: AbortSignal,
): Promise<Response> {
  const headers = await buildFinanceRequestHeaders();
  return fetch(`${resolveFinanceBaseUrl()}/api/traces/activity`, {
    method: 'GET',
    // Some proxies buffer SSE without an explicit event-stream Accept.
    headers: { ...headers, Accept: 'text/event-stream' },
    signal,
    cache: 'no-store',
  });
}

/**
 * `GET /api/mailbox/{id}/runs?limit=25` — the processed runs for one mailbox.
 * Returns the loader's discriminated `{ ok, items } | { ok:false, error }`
 * shape verbatim; the caller narrows the summary by the mailbox's kind.
 */
export async function getMailboxRuns<TSummary = TWireMailboxSummary>(
  mailboxId: string,
  limit = 25,
  signal?: AbortSignal,
): Promise<TWireMailboxRunsResponse<TSummary>> {
  const { data } = await financeApi.get<TWireMailboxRunsResponse<TSummary>>(
    `/api/mailbox/${encodeURIComponent(mailboxId)}/runs`,
    { params: { limit }, signal },
  );
  return data;
}

/** `GET /api/journal-entries/staged` — run short-id → its JE-register entry. */
export async function getStagedByRun(
  signal?: AbortSignal,
): Promise<IStagedByRunResponse> {
  const { data } = await financeApi.get<IStagedByRunResponse>(
    '/api/journal-entries/staged',
    { signal },
  );
  return data;
}

/**
 * `POST /api/journal-entries/register` — stage a mailbox run as a JE-register
 * draft. The body is the DraftInput the from-mailbox mappers produced.
 */
export async function stageJournalEntry(draft: IWireDraftInput): Promise<void> {
  await financeApi.post('/api/journal-entries/register', draft);
}

/** `POST /api/agents/{agentKey}/runs/{runShortId}/archive` — archive one run. */
export async function archiveMailboxRun(
  agentKey: string,
  runShortId: string,
): Promise<void> {
  await financeApi.post(
    `/api/agents/${encodeURIComponent(agentKey)}/runs/${encodeURIComponent(
      runShortId,
    )}/archive`,
  );
}

/**
 * Export the selected runs as an `.xlsx` workbook. Reclass mailboxes POST to
 * `/api/journal-entries/export`, statement mailboxes to `/statement-export`;
 * both take `{ items }` (the raw run+summary rows) and return a blob.
 */
export async function exportMailboxRuns(
  kind: 'reclass' | 'statement',
  items: unknown[],
): Promise<Blob> {
  const path =
    kind === 'statement'
      ? '/api/journal-entries/statement-export'
      : '/api/journal-entries/export';
  const { data } = await financeApi.post<Blob>(
    path,
    { items },
    { responseType: 'blob' },
  );
  return data;
}

// ── Agent builder ─────────────────────────────────────────────────────────────
//
// The builder's two provisioning endpoints stream SSE and are consumed with
// `useStreamRequest` (which needs `fetch`, not axios), so only their paths live
// here; the hook builds the URL with `resolveFinanceBaseUrl()` and auth with
// `buildFinanceRequestHeaders()`. `create-draft` is a plain JSON turn, so it goes
// through the shared axios instance like every other finance REST call.

/** SSE — `POST` builds + provisions the agent from the drafted SOP. */
export const FINANCE_AGENT_PROVISION_PATH = '/api/agents/provision-from-sop';
/** SSE — `POST` resumes Quill after an `interrupt` with the user's reply. */
export const FINANCE_AGENT_CONTINUE_QUILL_PATH = '/api/agents/continue-quill';

/** `POST /api/agents/create-draft` — one builder turn (draft or clarifying question). */
export async function createAgentDraft(
  body: ICreateAgentDraftRequest,
  signal?: AbortSignal,
): Promise<ICreateAgentDraftResponse> {
  const { data } = await financeApi.post<ICreateAgentDraftResponse>(
    '/api/agents/create-draft',
    body,
    { signal },
  );
  return data;
}

/** `GET /api/agents/process-catalog` — the APQC browse-tree taxonomy (hi-fi templates). */
export async function getProcessCatalog(
  signal?: AbortSignal,
): Promise<IProcessCatalogResponse> {
  const { data } = await financeApi.get<IProcessCatalogResponse>(
    '/api/agents/process-catalog',
    { signal },
  );
  return data;
}

/** `POST /api/agents/match-catalog` — rank the catalog against a freeform description. */
export async function matchCatalog(
  body: IMatchCatalogRequest,
  signal?: AbortSignal,
): Promise<IMatchCatalogResponse> {
  const { data } = await financeApi.post<IMatchCatalogResponse>(
    '/api/agents/match-catalog',
    body,
    { signal },
  );
  return data;
}
